import { convertReadableString } from "@utils/strings";

import { OrganizationTypeEnum } from "@api/rest/types-and-hooks";
import { EhrSyncMode } from "@pages/Organizations/constants";

/** Only Frontera EHR orgs take a vertical organization id from Basecamp. */
export const takesVerticalOrgId = (organizationType: OrganizationTypeEnum | null | undefined): boolean =>
  organizationType === OrganizationTypeEnum.FRONTERA_EHR;

// Mirrors frontera's `OrganizationTypeEnum.graduated_ehr_types()`. The Record
// forces a decision here whenever a new type is added to the generated enum.
const EHR_SYNC_MODE_BY_TYPE: Record<OrganizationTypeEnum, Exclude<EhrSyncMode, typeof EhrSyncMode.UNSELECTED>> = {
  [OrganizationTypeEnum.FRONTERA_HEALTH]: EhrSyncMode.EDITABLE,
  [OrganizationTypeEnum.FRONTERA_CLARITY]: EhrSyncMode.EDITABLE,
  [OrganizationTypeEnum.PASSAGE_HEALTH]: EhrSyncMode.ALWAYS_ON,
  [OrganizationTypeEnum.ARTEMIS]: EhrSyncMode.EDITABLE,
  [OrganizationTypeEnum.THERAPZ]: EhrSyncMode.EDITABLE,
  [OrganizationTypeEnum.HI_RASMUS]: EhrSyncMode.EDITABLE,
  [OrganizationTypeEnum.MOTIVITY]: EhrSyncMode.EDITABLE,
  [OrganizationTypeEnum.CENTRAL_REACH]: EhrSyncMode.EDITABLE,
  [OrganizationTypeEnum.MEASUREPM]: EhrSyncMode.EDITABLE,
  [OrganizationTypeEnum.ENSORA]: EhrSyncMode.EDITABLE,
  [OrganizationTypeEnum.HIPP_HEALTH]: EhrSyncMode.EDITABLE,
  [OrganizationTypeEnum.RETHINK]: EhrSyncMode.EDITABLE,
  [OrganizationTypeEnum.FRONTERA_EHR]: EhrSyncMode.EDITABLE,
};

export const getEhrSyncMode = (organizationType: OrganizationTypeEnum | null | undefined): EhrSyncMode =>
  // `?? EDITABLE`: a type the backend added before codegen ran isn't in the map.
  organizationType ? (EHR_SYNC_MODE_BY_TYPE[organizationType] ?? EhrSyncMode.EDITABLE) : EhrSyncMode.UNSELECTED;

/** The sync state an org of this mode actually has, given the stored or chosen value. */
export const resolveEhrSyncEnabled = (mode: EhrSyncMode, value: boolean): boolean =>
  mode === EhrSyncMode.ALWAYS_ON || (mode === EhrSyncMode.EDITABLE && value);

const ORGANIZATION_TYPE_LABEL_OVERRIDES: Partial<Record<OrganizationTypeEnum, string>> = {
  [OrganizationTypeEnum.FRONTERA_HEALTH]: "No EHR",
  [OrganizationTypeEnum.MEASUREPM]: "MeasurePM",
  [OrganizationTypeEnum.CENTRAL_REACH]: "CentralReach",
  [OrganizationTypeEnum.FRONTERA_EHR]: "Frontera EHR",
};

export const getOrganizationTypeLabel = (organizationType: OrganizationTypeEnum): string =>
  ORGANIZATION_TYPE_LABEL_OVERRIDES[organizationType] ?? convertReadableString(organizationType);

/**
 * Types Ops can pick when creating an org. Clarity orgs are created by the
 * Direct-to-Parent signup flow only, and the backend rejects them here.
 */
export const CREATABLE_ORGANIZATION_TYPES: OrganizationTypeEnum[] = Object.values(OrganizationTypeEnum).filter(
  (organizationType) => organizationType !== OrganizationTypeEnum.FRONTERA_CLARITY,
);

export const EHR_SYNC_COPY = {
  legend: "EHR sync",
  checkbox: "Enable EHR sync for this organization",
  tooltip:
    "Controls who can use EHR actions (connect, disconnect, import, sync, refresh). On: everyone in the organization. Off: only Frontera staff; customer users see these actions blocked with an explanation.",
  tooltipLabel: "About EHR sync",
  help: {
    [EhrSyncMode.EDITABLE]:
      "While off, only Frontera staff can use EHR actions for this organization. Turn on after Frontera validates its EHR connection.",
    [EhrSyncMode.ALWAYS_ON]: "Always on for Passage Health organizations.",
    [EhrSyncMode.UNSELECTED]: "Select an EHR system first.",
  } satisfies Record<EhrSyncMode, string>,
  changeLaterHint: "You can change this later in Settings.",
  createWarning: "Customer users will be able to use EHR actions as soon as this organization is created.",
  resetNotice: "EHR sync is off. It's only on automatically for Passage Health.",
  created: "Organization created.",
  createdWithSync: "Organization created. EHR sync is on.",
} as const;
